import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { mergeMap, Observable, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FormWizardDataModel, IFormWizardDataModel } from '../veg-wizard/models/form-wizard-models';
import { UtilService } from '../veg-wizard/services/util.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  sessionId?: string = undefined;
  sessionKey: string;

  constructor(protected http: HttpClient,
    private utilService: UtilService) {
    this.sessionKey = environment.sessionIdKey;
  }

  getSessionId(): string | undefined {
    return sessionStorage.getItem(this.sessionKey) ?? undefined;
  }

  getSessionIdOrError(): string {
    const sessionId = sessionStorage.getItem(this.sessionKey);
    if (!sessionId) throw new Error("Session Id must not be null when calling this method!");

    return sessionId;
  }

  private setSessionId(sessionId: string) {
    sessionStorage.setItem(this.sessionKey, sessionId);
    this.sessionId = sessionId;
  }

  startNewSessionIdAfterLodgement(sessionId: string) {
    sessionStorage.setItem(this.sessionKey, sessionId);
    this.sessionId = sessionId;
  }

  clearSession() {
    // nuke all the session storage data!
    sessionStorage.removeItem(environment.sessionIdKey);
    sessionStorage.removeItem(environment.sessionFormWizardDataModelKey);
    this.sessionId = undefined;
  }

  /**
   * Starts a session on the OSACN server returning its unique key. If
   * a session has already been started, it simply returns the id.
   */
  startOrGetSessionId$(): Observable<string> {
    if (this.sessionId === undefined) {
      this.sessionId = this.getSessionId(); // attempt to get SessionKey from sessionStorage
    }

    // if we don't have a session, get a new one and exit.
    if (!this.sessionId || !this.sessionId.trim()) {
      return this.startSession$();
    }

    const sessionId = this.sessionId as string;

    const isSessionValidUrl =
      `${environment.apiUrl}/session/is-session-valid?sessionKey=` +
      encodeURIComponent(sessionId);

    return this.http.get<boolean>(isSessionValidUrl).pipe(
      mergeMap((isSessionIdValid) => {
        if (!isSessionIdValid) {
          this.sessionId = undefined;
          return this.startSession$();
        }

        return of(sessionId);
      })
    );
  }

  getSessionData(): IFormWizardDataModel {
    const sessionStringData = sessionStorage.getItem(environment.sessionFormWizardDataModelKey);
    if (sessionStringData) {
      const sessionData = JSON.parse(sessionStringData) as IFormWizardDataModel;
      return sessionData;
    }

    const fwdm = new FormWizardDataModel();
    return fwdm;
  }

  setSessionData$(data: IFormWizardDataModel): Observable<boolean> {
    sessionStorage.setItem(environment.sessionFormWizardDataModelKey, JSON.stringify(data));
    return of(true);
  }

  destroyAndResetSession() {
    sessionStorage.clear();
  }

  private startSession$(): Observable<string> {
    const url = `${environment.apiUrl}/session/start-session`;
    return this.http.post<string>(url, null).pipe(
      tap((sessionId) => {
        this.setSessionId(sessionId);
      })
    );
  }

}
